import { css } from '@emotion/react';
import { colors } from '../../../../theme/colors';
import { transparentBgStyle } from '../../../../theme/component-styles';

/**
 * Shared expandable card container style.
 *
 * Expects the host element to set `data-expanded` when the card is open,
 * so that the hover background only applies when collapsed.
 *
 * Usage:
 *   <div css={expandableCardCss} data-expanded={isExpanded || undefined}>
 */
export const expandableCardCss = css`
  background: ${transparentBgStyle.background};
  border: 0.5px solid color-mix(in srgb, var(--mui-palette-primary-main) 12%, transparent);
  border-radius: 4px;
  padding: 0.75rem 1rem;

  &:not([data-expanded]):hover {
    background: var(--mui-palette-action-hover);
  }
`;

/**
 * Hover and selected chrome shared by the round cards in the Deals-tab list and
 * the Activity-drawer Deal Docs panel, so one selection reads as one state on
 * both surfaces. Layer after `expandableCardCss`; the selectors match its
 * `:not([data-expanded]):hover` so source order decides.
 */
export const roundCardHoverCss = css`
  &:not([data-expanded]):hover {
    background: ${colors.surface.cardHover};
    border-color: ${colors.border.cardHover};
  }
`;

export const roundCardSelectedCss = css`
  border-color: ${colors.border.cardSelected};
  background: ${colors.surface.cardSelected};

  &:not([data-expanded]):hover {
    background: ${colors.surface.cardSelected};
    border-color: ${colors.border.cardSelected};
  }
`;

/**
 * Shared header style for expandable cards.
 *
 * Designed for a `<button>` element (proper a11y) with full-width flex layout.
 * Callers place their title JSX and chevron icon as children.
 */
export const expandableCardHeaderCss = css`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  cursor: pointer;
  user-select: none;
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  color: inherit;
`;

/**
 * Chevron style for expand/collapse icons (ExpandMoreIcon).
 *
 * Collapsed: points right (-90deg). Expanded: points down (default orientation).
 * Reads expanded state from the nearest ancestor with `data-expanded`.
 */
export const expandChevronCss = css`
  transition: transform 0.1s;
  transform: rotate(-90deg);
  color: rgba(75, 82, 93, 0.64);

  [data-expanded] & {
    transform: rotate(0deg);
  }
`;

export const mutedCaptionCss = css`
  font-size: 0.75rem;
  line-height: 1.66;
  color: rgba(63, 73, 89, 0.64);
  letter-spacing: 0.1px;
`;

/**
 * Shared empty-state styles for activity drawer tabs.
 *
 * Use as a trio: a centered container (`emptyStateCss`) wrapping a title
 * (`emptyTitleCss`) and supporting body text (`emptyTextCss`). Children
 * may override the inherited container color.
 */
export const emptyStateCss = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  text-align: center;
  color: rgba(63, 73, 89, 0.64);
`;

export const emptyTitleCss = css`
  font-size: 16px;
  line-height: 1.75;
  color: rgba(16, 37, 62, 0.87);
  margin-bottom: 8px;
`;

export const emptyTextCss = css`
  font-size: 14px;
  line-height: 1.43;
  color: rgba(63, 73, 89, 0.64);
`;

export const documentItemCardCss = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 0.75rem;
  background: ${transparentBgStyle.background};
  border: 0.5px solid color-mix(in srgb, var(--mui-palette-primary-main) 12%, transparent);
  border-radius: 4px;

  &:hover {
    background: var(--mui-palette-action-hover);
  }
`;
